'use client';

import type { IconName } from '@/components/entities/Sprite/Icon';
import type { ModalNameType } from '@/modals/components/ModalProvider';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { GmBarDesktop } from '@/components/features/gamification/bars/components/GmBarDesktop';
import { useUnclaimedGamificationRewardsCount } from '@/components/features/gamification/modules/rewards/hooks/useUnclaimedGamificationRewardsCount';
import { ACCOUNT_LINKS, PROTECTED_LINKS } from '@/components/features/layout/Navigation/consts/links';
import { isNavigationLinkActive } from '@/components/features/layout/Navigation/utils/isNavigationLinkActive';
import { useSidebarGroupCategories } from '@/hooks/useSideBarGroupCategories';
import { AppRoutes } from '@/i18n/constants';
import { usePathname } from '@/i18n/routing';
import CategoryItem from './components/CategoryItem';
import NavigationItem from './components/NavigationItem';
import OtherCategoriesAccordion from './components/OtherCategoriesAccordion';
import PwaInstallNavigationItem from './components/PwaInstallNavigationItem';

interface Props {
  isMenuMinify: boolean;
  handleCloseSideBar: () => void;
}

const NavigationItems = ({ isMenuMinify, handleCloseSideBar }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const pathname = usePathname();
  const categoryId = useParams()?.id as string;
  const { totalRewards } = useUnclaimedGamificationRewardsCount();

  const { beforeFavoritesCategories, afterFavoritesCategories, otherCategories } = useSidebarGroupCategories();

  useEffect(() => {
    const allLinks = [...PROTECTED_LINKS, ...ACCOUNT_LINKS].filter(link => link.href);
    const isLinkActive = allLinks.some(link => isNavigationLinkActive(link, pathname));

    if (!isLinkActive) {
      setActiveId(null);
    }
  }, [pathname]);

  const getBadge = (link: typeof PROTECTED_LINKS[number]) => {
    if (link.href === AppRoutes.gamificationRewards) {
      return totalRewards !== 0 ? totalRewards.toString() : undefined;
    }
    return link.badge;
  };

  return (
    <div className="sticky top-0 lg:pb-4 md:pb-16 pt-4 px-4 md:px-1 max-md:pt-16 flex flex-col md:pt-6 3xl:pt-0 3xl:px-0">

      <div className="flex flex-col 3xl:gap-4">
        <div className="flex flex-col mt-4 md:gap-1 md:mt-0 3xl:px-1 order-2">
          {PROTECTED_LINKS.map(link => (
            <NavigationItem
              key={link.label}
              label={link.label}
              icon={link.icon as IconName}
              href={link.href}
              modal={link.modal as ModalNameType}
              badge={getBadge(link)}
              variant="primary"
              activeId={activeId}
              isActive={isNavigationLinkActive(link, pathname)}
              setActiveId={setActiveId}
              handleCloseSideBar={handleCloseSideBar}
            />
          ))}
          <div className="mt-4 mb-3.75 h-px bg-c300 opacity-30 -mx-4 md:-mx-2 3xl:-mx-1 md:mt-3" />
        </div>

        <div className="order-1 md:rounded-none max-md:-mx-1.5 rounded-2xl md:overflow-visible overflow-hidden md:mt-0 mt-4 md:-mx-1 3xl:mx-0 mb-4">
          <GmBarDesktop />
        </div>

      </div>
      <div className="flex flex-col gap-1 relative md:gap-1 3xl:px-1">
        {beforeFavoritesCategories?.map(category => (
          <CategoryItem
            key={category.id}
            category={category}
            categoryId={categoryId}
            handleCloseSideBar={handleCloseSideBar}
            isMenuMinify={isMenuMinify}
          />
        ))}
        <NavigationItem
          label="Favorites"
          icon="favorites"
          href="/category/favorites-games"
          variant="account"
          isActive={pathname === '/category/favorites-games'}
          handleCloseSideBar={handleCloseSideBar}
        />

        {afterFavoritesCategories?.map(category => (
          <CategoryItem
            key={category.id}
            category={category}
            categoryId={categoryId}
            handleCloseSideBar={handleCloseSideBar}
            isMenuMinify={isMenuMinify}
          />
        ))}
        {otherCategories && otherCategories.length > 0 && (
          <OtherCategoriesAccordion
            categoryId={categoryId}
            handleCloseSideBar={handleCloseSideBar}
            isMenuMinify={isMenuMinify}
            otherCategories={otherCategories}
          />
        )}
      </div>
      <div className="h-px bg-c300 opacity-30 -mx-4 md:-mx-2 3xl:-mx-1 mt-4 mb-3.75" />

      <div className="flex flex-col gap-1 max-md:pb-5.5 3xl:px-1">
        {ACCOUNT_LINKS.map(link => (
          <NavigationItem
            key={link.label}
            label={link.label}
            icon={link.icon as IconName}
            href={link.href}
            modal={link.modal as ModalNameType}
            variant="account"
            activeId={activeId}
            setActiveId={setActiveId}
            handleCloseSideBar={handleCloseSideBar}
          />
        ))}
        <PwaInstallNavigationItem handleCloseSideBar={handleCloseSideBar} />
      </div>
    </div>
  );
};

export default NavigationItems;
